/* G-String product choices (blocks/gs-product-choices.liquid):
   - fills bow details from a "what to order" link (?bow=…&string=…&number=…&size=…&spec=…)
   - "Pick your Oneida" on Oneida string and cable listings: fills the order like the Oneida model page does (the timing
     cable: the bow only), starting on the bow the customer picked before
   - "Your bow's brand" on a lever-bow string listing that fits our bows or other brands' too: the brand first, then its
     questions (ours: the model and an optional bow number, looked up; Oneida: the box above; another brand: its model)
   - sends a one-key listing's bow key (gstring.bow_key: the G-Rex, Pro2G and GSB timing cables), and on our own bows'
     other cable listings the key of the bow whose number is typed (g-rex-1285), said back once it's looked up
   - draws the Flemish string in the picked string, whisker and slick colors */
(() => {
  const root = document.querySelector('[data-gs-choices]');
  if (!root) return;
  const q = new URLSearchParams(location.search);

  /* ---------- bow details from the link ---------- */
  const ctx = {
    bow: q.get('bow') || '',
    string_length: q.get('string') ? q.get('string').replace(/[″"]+$/, '') + '″' : '',
    bow_number: q.get('number') || '',
    bow_size: q.get('size') || '',
  };
  root.querySelectorAll('[data-gs-autofill]').forEach(inp => {
    if (inp.value) return;
    const v = (inp.dataset.gsAutofill || '').split(',').map(k => ctx[k.trim()] || '').filter(Boolean).join(' · ');
    if (v) inp.value = v.slice(0, Number(inp.maxLength) > 0 ? inp.maxLength : 100);
  });
  const spec = root.querySelector('[data-gs-spec]');
  // The bow key only ever names a bow (e.g. "g-rex-1285", "aeroforce-2" or "phoenix-medium"); the shop's build sheet uses it.
  // A key from the link is only sent while the bow boxes still say what the link filled in: a different bow typed in = no key.
  // A size picked in "Pick your Oneida" (below) sends its own key, the one its model page sends.
  const filled = [...root.querySelectorAll('[data-gs-autofill]')].map(i => [i, i.value]);
  const linkSpec = /^[a-z0-9-]{1,60}$/.test(q.get('spec') || '') ? q.get('spec') : '';
  let pickSpec = '';
  // a choice can add to the bow key: Dead Wake Archery's outer limbs ("machined"/"standard") decide the yoke length
  // (The aluminum timing wheels' _spec is their own, data-gs-wheelup-spec, run by gstring.js: the wheels are a part, so
  // this block gives them no [data-gs-spec] and setSpec leaves theirs alone.)
  const specPick = root.querySelector('[data-gs-spec-pick]');
  // A listing that fits every bow of one model the same way has its own key (gstring.bow_key: the G-Rex, Pro2G and GSB
  // timing cables, Joey Oct 25 2026), sent when no pick or link gives one; the page starts with it already filled in
  const fixedSpec = /^[a-z0-9-]{1,60}$/.test(spec?.dataset.gsSpecFixed || '') ? spec.dataset.gsSpecFixed : '';
  const setSpec = () => {
    if (!spec) return;
    const base = pickSpec || (linkSpec && !filled.some(([i, v]) => i.value !== v) ? linkSpec : '') || fixedSpec;
    const suf = specPick?.selectedOptions[0]?.dataset.spec || '';
    spec.value = base && suf ? base + '-' + suf : base;
    spec.disabled = !base; // no bow key: nothing sent
  };
  root.addEventListener('input', setSpec);
  root.addEventListener('change', setSpec);
  window.addEventListener('pageshow', setSpec); // a choice the browser put back (reload, back button) fires no change
  setSpec();

  // the shop's numbers: 1285, or letters then digits like JC067 (the same reading as Find my bow, gstring.js numOf)
  const numOf = t => {
    const raw = String(t || '').trim().toUpperCase().replace(/^#/, '').replace(/\s+/g, '')
      .replace(/^(JC|W)([0-9O]+)$/, (_, p, n) => p + n.replace(/O/g, '0'));
    return /^\d+$/.test(raw) ? String(parseInt(raw, 10)) : raw;
  };
  const slug = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  /* ---------- required boxes need real text, not just spaces ---------- */
  const needText = i => i.setCustomValidity(i.value.trim() ? '' : 'Please fill this in.');
  const trimmed = i => {
    i.addEventListener('input', () => needText(i));
    i.addEventListener('change', () => { i.value = i.value.trim(); needText(i); });
  };
  root.querySelectorAll('input[required]').forEach(i => { trimmed(i); needText(i); });

  /* ---------- Pick your Oneida (Joey, Oct 24 2026): orders typed in on these pages carried no bow key, so the build
     sheet couldn't show the cable lengths. A model and size fill the same order lines the model page sends (bow, size,
     string length, String material, the Ospreys' question, _spec); the data comes from the block, built with the model
     page's own Liquid. "My bow isn't listed" shows the typed boxes again, with what the customer typed in them.
     The timing cable needs only the bow (Joey, Oct 25 2026: "make it a pick your bow with all the bows in a drop down
     unless a bow is selected already through the find my bow then it should auto fill"): no size, and the model's own
     timing key ("phoenix-timing"). Every picker starts on the bow picked before (gstring.js GSBow, "gs:bow").
     On a string listing with the brand pick (below), this box is its Oneida answer: it shows, counts and sends only while
     Oneida is the brand, and the brand pick's "Another lever bow" stands for "My bow isn't listed". ---------- */
  const pickBox = root.querySelector('[data-gs-oneida-pick]');
  const brandBox = root.querySelector('[data-gs-brand-pick]');
  // hidden lines, sent only with a pick (the block renders the ones this listing's kind takes)
  const sends = {
    string: root.querySelector('[data-gs-oneida-string]'),
    material: root.querySelector('[data-gs-oneida-material]'),
    cable: root.querySelector('[data-gs-oneida-cable-material]'),
  };
  const typed = [...root.querySelectorAll('[data-gs-oneida-typed]')].map(box => ({ box, inp: box.querySelector('input') })).filter(t => t.inp);
  const isString = root.dataset.role === 'string';
  const isTiming = root.dataset.role === 'timing_cable';
  const mem = window.GSBow || null; // the remembered bow (no storage, or gstring.js didn't load: nothing pre-filled)
  // the box's value the bow's page would send: its autofill keys, joined like gstring.js setBowContext
  const fillFor = (inp, s) => (inp.dataset.gsAutofill || '').split(',').map(k => {
    k = k.trim();
    if (k === 'string_length') return s.string ? s.string + '″' : '';
    return k === 'bow' ? s.bow : k === 'bow_size' ? s.size : '';
  }).filter(Boolean).join(' · ');
  const send = (inp, v) => { if (inp) { inp.value = v || ''; inp.disabled = !v; } };
  const para = parts => { const p = document.createElement('p'); p.append(...parts); return p; };
  const bold = t => { const b = document.createElement('b'); b.textContent = t; return b; };
  // Dacron strings (the original Tomcat and the H250) come in the solid colors only (Joey, Oct 27 2026, #210: "SHORTER LIST
  // AND ONLY SOLID COLORS FOR NOW NO MIXES OR SPECS"): a color marked for a string material it isn't made in (data-not-on,
  // the mixed colors on Dacron) is taken out of the box while a bow with that material is picked (taken out, not hidden: iPhones
  // still list a hidden option), and if it was the pick, the box goes back to "Choose…"
  const matSelects = [...root.querySelectorAll('select[data-gs-opt]')].filter(sel => sel.querySelector('option[data-not-on]'));
  const allOpts = new Map(matSelects.map(sel => [sel, [...sel.options]]));
  const setMaterial = mat => matSelects.forEach(sel => {
    const keep = allOpts.get(sel).filter(o => o.dataset.notOn !== mat);
    if (keep.length === sel.options.length) return; // nothing to take out or put back
    const was = sel.value;
    sel.replaceChildren(...keep);
    sel.value = keep.some(o => o.value === was) ? was : '';
    if (sel.value !== was) sel.dispatchEvent(new Event('change', { bubbles: true }));
  });
  // the typed boxes: filled from the pick and hidden, or shown with the customer's own text (typing in their bow, or
  // nothing on file for that box); nothing picked yet: hidden and not sent (the dropdowns ask first). quiet: a box with
  // nothing on file stays hidden and unsent too (the brand pick's "not on file yet" box asks for it instead)
  const fillTyped = (s, typing, quiet) => typed.forEach(({ box, inp }) => {
    const v = s ? fillFor(inp, s) : '';
    const own = typing || (!!s && !v && !quiet); // nothing on file for this box: the customer types it
    if (v && !typing) {
      if (inp.dataset.gsPicked !== '1') inp.dataset.gsTyped = inp.value; // kept for "My bow isn't listed"
      inp.value = v;
      inp.dataset.gsPicked = '1';
    } else if (inp.dataset.gsPicked === '1') {
      inp.value = inp.dataset.gsTyped || '';
      delete inp.dataset.gsPicked;
    }
    box.hidden = !own;
    inp.disabled = !own && !v;
    if (inp.required) needText(inp);
  });

  let oneida = null; // the Oneida box, for the brand pick
  let onOneidaPick = null; // the brand pick's "it's their pick now" (its remembered note goes)
  let models = [];
  try { models = JSON.parse(pickBox?.querySelector('[data-gs-oneida-models]')?.textContent || '[]'); } catch (e) { models = []; } // the typed boxes still work
  if (pickBox && models.length) {
    const modelSel = pickBox.querySelector('[data-gs-oneida-model]');
    const sizeSel = pickBox.querySelector('[data-gs-oneida-size]'); // none on the timing cable
    const sizeBox = pickBox.querySelector('[data-gs-oneida-size-box]');
    const answer = pickBox.querySelector('[data-gs-oneida-answer]');
    const askBox = pickBox.querySelector('[data-gs-oneida-ask]');
    const ask = askBox.querySelector('[data-gs-oneida-ask-input]');
    const askHint = askBox.querySelector('[data-gs-oneida-ask-hint]');
    const remembered = pickBox.querySelector('[data-gs-oneida-remembered]');
    trimmed(ask);
    let on = !brandBox; // under the brand pick: only while Oneida is the brand

    models.forEach(m => modelSel.append(new Option(m.name, m.h)));
    if (!brandBox) modelSel.append(new Option(modelSel.dataset.gsTypeLabel || 'My bow isn\'t listed', 'type'));
    modelSel.required = true;

    const model = () => models.find(m => m.h === modelSel.value) || null;
    const fillSizes = m => {
      if (!sizeSel) return;
      sizeSel.replaceChildren();
      if (!m) return;
      if (m.sizes.length > 1) sizeSel.append(new Option('Choose…', ''));
      let group = '', parent = sizeSel;
      m.sizes.forEach((s, n) => {
        if (s.group !== group) { // the model page's group headings ("Old I-Cam…", the H500's limb sets)
          group = s.group;
          parent = sizeSel;
          if (group) { parent = document.createElement('optgroup'); parent.label = group; sizeSel.append(parent); }
        }
        const draw = s.draw && !s.label.includes(s.draw) ? ' · ' + s.draw + '″ draw' : ''; // the H500's sizes are named by draw
        // the string length too (Joey, Oct 24 2026: "its easier to pick your size if you can see the string length with it")
        const string = s.string && !s.label.includes(s.string) ? ' · ' + s.string + '″ string' : ''; // split sizes already name it
        parent.append(new Option(s.label + string + draw, String(n)));
      });
      if (m.sizes.length === 1) sizeSel.value = '0'; // one size: it's picked, like the model page
    };
    // what's picked: a size, or on the timing cable the bow itself, with the model's own timing key (the one its model
    // page's timing card sends). Never a length.
    const picked = m => {
      if (!m) return null;
      if (isTiming) return { bow: m.name, size: '', string: '', spec: m.h + '-timing' };
      return sizeSel.value !== '' ? m.sizes[Number(sizeSel.value)] || null : null;
    };

    const apply = () => {
      if (!on) { // the brand pick is on another brand: none of these questions count, and it sends its own lines
        [modelSel, sizeSel, ask].forEach(c => { if (c) { c.disabled = true; c.required = false; } });
        answer.replaceChildren();
        return;
      }
      modelSel.disabled = false;
      modelSel.required = true;
      const typing = modelSel.value === 'type';
      const m = model();
      const s = picked(m);
      if (sizeBox) {
        sizeBox.hidden = !m;
        sizeSel.disabled = !m;
        sizeSel.required = !!m;
      }
      if (m) {
        pickBox.querySelectorAll('[data-gs-oneida-link]').forEach(a => { a.href = m.url || '/pages/find-my-bow'; });
        pickBox.querySelectorAll('[data-gs-oneida-name]').forEach(n => { n.textContent = m.name; });
      }
      fillTyped(s, typing);
      // the lines the model page's card sends with the size
      send(sends.string, s && s.string ? s.string + '″' : '');
      send(sends.material, s && isString ? m.string_material : '');
      send(sends.cable, s && !isString ? m.cable_material : '');
      if (isString) setMaterial(m ? m.string_material : '');
      // the Ospreys: "Your current string length and size", required, as on their page. Not on the timing cable: every
      // Osprey takes the same one ("all osprey talon hawks no matter the years get the same timing cable length", Oct 24)
      const wantAsk = !!(m && m.ask) && !isTiming;
      askBox.hidden = !wantAsk;
      ask.disabled = !wantAsk;
      ask.required = wantAsk;
      if (wantAsk) {
        ask.name = 'properties[' + m.ask + ']';
        askBox.querySelector('[data-gs-oneida-ask-label]').textContent = m.ask;
        askHint.textContent = m.ask_hint || '';
        // filled in from the pick (Joey, Oct 24 2026: "prefill the current string length"); the customer can still
        // change it, and their own text is never written over
        const fill = s && s.string ? s.string + '″ ' + s.size : '';
        if (!ask.value.trim() || ask.value === ask.dataset.gsPrefill) { ask.value = fill; ask.dataset.gsPrefill = fill; }
        needText(ask);
      }
      pickSpec = s ? s.spec : '';
      setSpec();
      // what's picked, said back (a status line, so screen readers hear it too). Never a cable length.
      const lines = [];
      if (s) {
        lines.push(para([bold(s.bow), s.string ? ': ' + s.string + '″ string' : '']));
        const made = m.cable_material ? ' Made from ' + m.cable_material + ', not steel cable.' : '';
        if (isString) lines.push(para(['Built on ' + m.string_material + (m.string_material === 'Dacron' ? ' (plastic limb tips). Dacron strings don\'t come in the mixed colors (like Red & Black).' : '.')]));
        else if (isTiming) lines.push(para(['We build your timing cable to our length for this bow.' + made]));
        else lines.push(para(['We build your cables to our lengths for this size.' + made]));
      } else if (typing) {
        if (typed.length) lines.push(para(['Type your bow in below.']));
        else {
          const a = document.createElement('a');
          a.href = '/pages/find-my-bow';
          a.textContent = 'Find your bow';
          lines.push(para([a, ' and order from its page, or call or text us at 270-318-2020.']));
        }
      }
      answer.replaceChildren(...lines);
    };
    // pylon Oneidas: the cable pictures and "where it goes" drawings switch to pylon-style (gstring.js), like on their page
    const showKind = m => {
      const b = m && document.querySelector('[data-gs-cstack-show="' + (m.pylon ? 'pylon' : 'std') + '"]');
      if (b && b.getAttribute('aria-pressed') !== 'true') b.click();
    };
    // a pick here is remembered for the next picker and the model page (the timing cable keeps the size remembered before)
    const remember = () => {
      const m = model();
      if (!m || !mem) return;
      const s = picked(m);
      mem.set(m.h, isTiming ? undefined : s ? s.spec : '');
    };
    // once they change anything, it's their pick now, not the remembered one
    const forget = () => {
      if (!remembered || remembered.hidden) return;
      remembered.hidden = true;
      modelSel.removeAttribute('aria-describedby');
    };
    modelSel.addEventListener('change', () => { forget(); onOneidaPick?.(); fillSizes(model()); apply(); showKind(model()); remember(); });
    sizeSel?.addEventListener('change', () => { forget(); onOneidaPick?.(); apply(); remember(); });

    // a link whose bow key is one of these sizes (or, on the timing cable, a model's timing key)
    const linkHit = linkSpec && (models.flatMap(m => m.sizes.map((s, n) => [m, n, s])).find(([, , s]) => s.spec === linkSpec)
      || (isTiming && models.filter(m => m.h + '-timing' === linkSpec).map(m => [m, -1])[0]));
    const pickHit = hit => {
      modelSel.value = hit[0].h;
      fillSizes(hit[0]);
      if (sizeSel && hit[1] >= 0) sizeSel.value = String(hit[1]);
      return hit[0];
    };
    // the bow picked before (and its size, if it's this model's); quiet: the brand pick says so itself
    const pickRemembered = quiet => {
      const r = mem?.get();
      const m = (r && models.find(x => x.h === r.model)) || null;
      if (!m) return null;
      modelSel.value = m.h;
      fillSizes(m);
      const n = m.sizes.findIndex(z => z.spec === r.size);
      if (sizeSel && n >= 0) sizeSel.value = String(n);
      if (remembered && !quiet) { remembered.hidden = false; modelSel.setAttribute('aria-describedby', remembered.id); }
      return m;
    };
    oneida = {
      linkHit: () => !!linkHit,
      remembers: () => { const r = mem?.get(); return !!(r && models.some(x => x.h === r.model)); },
      start: how => { // under the brand pick, once it has picked Oneida: 'link' or 'mem'
        const m = how === 'link' && linkHit ? pickHit(linkHit) : how === 'mem' ? pickRemembered(true) : null;
        if (m) showKind(m);
      },
      remember: () => { if (on) remember(); },
      setOn: v => {
        on = v;
        pickBox.hidden = !v;
        apply();
        if (v) showKind(model());
      },
    };
    if (!brandBox) {
      // start: a link to one of these sizes picks it; any other bow from a link stays typed in, as before; with no
      // link, the bow picked before
      let start = null;
      if (linkHit) start = pickHit(linkHit);
      else if (Object.values(ctx).some(Boolean)) modelSel.value = 'type';
      else start = pickRemembered(false);
      apply();
      if (start) showKind(start);
      pickBox.hidden = false;
    } else {
      apply();
    }
  }

  /* ---------- Your bow's brand (Joey, Oct 26 2026: "MAKE IT PICK YOUR BOW OR LEVERBOW. MAYBE A PCIK YOUR BRAND AND SO ON";
     blocks/gs-product-choices.liquid BRAND PICK). The brand first, then its questions, each sending what that bow's own
     page sends for this string (snippets/gs-buy-card):
     - G-String: the model (G-Rex, Pro2G, Gnat, and the GSB typed in, #241), and the number under the grip if they want
       (Joey, Oct 26 2026: "I DONT WANT THE NUMBER FOR OUR BOWS REQUIRED BUT I WANT IT AS AN OPTION"). A number is looked
       up like the Parts page does (/pages/bow/<model>-<number>?section_id=gs-bow-json): on file, the bow's string length
       and key go on the order (Bow Model "G-Rex #1285", String length, String material BCY 452X, _spec "g-rex-1285").
       Found with no string length on file (or "Measure tip to tip", which gs-bow-json leaves out): the bow page's
       "not on file yet" order, the customer's string length and tip to tip (_no_spec flags it for the build sheet and
       the record-specs tag). No number, or one we don't have online (and the GSB): the bow's name goes on the order and
       the customer types the string length in the String Length box (no key: the record-specs tag asks Joey to check).
     - Oneida: the Pick your Oneida box above.
     - Another brand: its model, and size when it has more than one, named like its model page ("Dead Wake Archery
       Kraken Medium"), with the size's string length and the model's key ("dwai-kraken"); no length on file: its page's
       "not on file yet" order (the customer's length, _no_spec).
     - Another lever bow: the typed boxes. A recurve or longbow: the recurve listing, and Add to cart stops here.
     It starts on a "what to order" link's bow, else on the bow picked before (gs:bow: an Oneida model, a brand model's
     key, or one of ours: "g-rex" or "g-rex-1285"), and every pick is remembered for next time. ---------- */
  let brandData = null;
  try { brandData = JSON.parse(brandBox?.querySelector('[data-gs-brand-data]')?.textContent || 'null'); } catch (e) { brandData = null; }
  if (brandBox && brandData) {
    const brandSel = brandBox.querySelector('[data-gs-brand]');
    const modelBox = brandBox.querySelector('[data-gs-brand-model-box]');
    const modelSel = brandBox.querySelector('[data-gs-brand-model]');
    const modelLabel = brandBox.querySelector('[data-gs-brand-model-label]');
    const sizeBox = brandBox.querySelector('[data-gs-brand-size-box]');
    const sizeSel = brandBox.querySelector('[data-gs-brand-size]');
    const numBox = brandBox.querySelector('[data-gs-brand-number-box]');
    const num = brandBox.querySelector('[data-gs-brand-number]');
    const numHint = brandBox.querySelector('[data-gs-brand-number-hint]');
    const answer = brandBox.querySelector('[data-gs-brand-answer]');
    const remembered = brandBox.querySelector('[data-gs-brand-remembered]');
    // "I want custom lengths" (snippets/gs-custom-lengths): with a bow key but no length on file it turns into the bow
    // page's "We don't have this one on file yet" box (gstring.js setNospec), and on our bows it asks the tip to tip too
    const cbox = root.querySelector('[data-gs-custom]');
    const cboxNospec = !!cbox?.hasAttribute('data-gs-nospec'); // the listing's own "no spec" (not on this one today)
    const tipParts = cbox ? [...cbox.querySelectorAll('[data-gs-tip]')] : [];
    const tipOff = cbox ? [...cbox.querySelectorAll('[data-gs-tip-off]')] : []; // the box's other lead line
    const tipInput = cbox?.querySelector('[data-gs-tip-input]');
    const OURS = brandData.gstring || [];
    const TRAD = brandData.trad || '';
    // the brands in the brand pages' order, each with its models in theirs
    const brands = [];
    (brandData.brands || []).slice().sort((a, b) => a.brand_sort - b.brand_sort || a.sort - b.sort || a.name.localeCompare(b.name)).forEach(m => {
      let b = brands.find(x => x.h === m.brand);
      if (!b) brands.push(b = { h: m.brand, name: m.brand_name, models: [] });
      b.models.push(m);
    });
    const allModels = brands.flatMap(b => b.models);
    if (OURS.length) brandSel.append(new Option('G-String (' + OURS.map(g => g.name).join(', ') + ')', 'gstring'));
    if (oneida) brandSel.append(new Option('Oneida', 'oneida'));
    brands.forEach(b => brandSel.append(new Option(b.name, 'b:' + b.h)));
    if (brandData.typed && typed.length) brandSel.append(new Option('Another lever bow: I\'ll type it in', 'other'));
    if (TRAD) brandSel.append(new Option('A recurve or longbow', 'trad'));
    brandSel.required = true;

    const brandOf = () => brands.find(b => 'b:' + b.h === brandSel.value) || null;
    const ours = () => (brandSel.value === 'gstring' && OURS.find(g => g.key === modelSel.value)) || null;
    const theirs = () => (brandOf()?.models.find(m => m.h === modelSel.value)) || null;
    const fillModels = () => {
      modelSel.replaceChildren(new Option('Choose…', ''));
      const b = brandOf();
      if (brandSel.value === 'gstring') OURS.forEach(g => modelSel.append(new Option(g.name, g.key)));
      else if (b) b.models.forEach(m => modelSel.append(new Option(m.name, m.h)));
      if (modelSel.options.length === 2) modelSel.value = modelSel.options[1].value; // one model (the Mega-Rex): it's picked
      fillSizes();
    };
    const fillSizes = () => {
      sizeSel.replaceChildren();
      const m = theirs();
      if (!m || m.sizes.length < 2) return;
      sizeSel.append(new Option('Choose…', ''));
      m.sizes.forEach((s, n) => sizeSel.append(new Option(s.label + (s.string && !s.label.includes(s.string) ? ' · ' + s.string + '″ string' : ''), String(n))));
    };

    // the number lookup: key -> the record's public details, { found, title, string_length }
    const looked = {};
    let lookSeq = 0;
    let looking = '';
    const recKey = () => {
      const g = ours();
      const n = numOf(num.value);
      return g && g.lookup && n ? g.key + '-' + slug(n) : '';
    };
    const lookup = async () => {
      const key = recKey();
      if (!key || key in looked || key === looking) return;
      const seq = ++lookSeq;
      looking = key;
      apply();
      let r = null;
      try {
        const res = await fetch('/pages/bow/' + encodeURIComponent(key) + '?section_id=gs-bow-json', { signal: AbortSignal.timeout(8000) });
        const m = res.ok && (await res.text()).match(/<script type="application\/json" data-gs-bow-json>([\s\S]*?)<\/script>/);
        r = m ? JSON.parse(m[1]) : { found: false };
        if (!res.ok && res.status !== 404) r = { error: true }; // the store didn't answer: try again on the next change
      } catch (e) { r = { error: true }; }
      if (!r.error) looked[key] = r;
      if (seq !== lookSeq) return; // another number went to be looked up meanwhile
      looking = '';
      const current = brandSel.value === 'gstring' && recKey() === key; // still the pick (not another brand, number or model)
      apply(current && r.error);
      if (current) remember(); // found: their bow, by its number, for next time
    };

    // what's picked, as the bow's page would send it (null: not enough picked yet)
    const pick = () => {
      if (brandSel.value === 'gstring') {
        const g = ours();
        if (!g) return null;
        const n = numOf(num.value);
        const s = { bow: g.name + (n ? ' #' + n : ''), size: '', string: '', spec: '', material: 'BCY 452X', ours: g, n };
        const key = recKey();
        if (key && key === looking) { s.pending = true; return s; } // looking it up: the String Length box stays meanwhile
        const r = key && looked[key];
        if (r && r.found) {
          s.spec = key;
          s.string = r.string_length ? String(r.string_length).replace(/[″"]+$/, '') : '';
          s.found = true;
        } else if (r) s.missing = true;
        return s;
      }
      const m = theirs();
      if (!m) return null;
      const sz = m.sizes.length === 1 ? m.sizes[0] : m.sizes.length && sizeSel.value !== '' ? m.sizes[Number(sizeSel.value)] || null : null;
      if (m.sizes.length > 1 && !sz) return null;
      return { bow: sz ? sz.bow : m.bow, size: m.sizes.length > 1 && sz ? sz.label : '', string: sz ? sz.string : '', spec: m.h, material: m.material };
    };

    const apply = failed => {
      const v = brandSel.value;
      const isOurs = v === 'gstring';
      const isTheirs = v.startsWith('b:');
      // a recurve or longbow: the other listing, and this one can't be added to the cart for it
      brandSel.setCustomValidity(v === 'trad' ? 'Recurve and longbow strings have their own listing: see the link below.' : '');
      if (oneida) oneida.setOn(v === 'oneida');
      modelBox.hidden = !(isOurs || isTheirs);
      modelSel.disabled = modelBox.hidden;
      modelSel.required = !modelBox.hidden;
      modelLabel.textContent = isOurs ? 'Which G-String bow' : 'Model';
      const m = isTheirs ? theirs() : null;
      sizeBox.hidden = !(m && m.sizes.length > 1);
      sizeSel.disabled = sizeBox.hidden;
      sizeSel.required = !sizeBox.hidden;
      if (m) {
        brandBox.querySelectorAll('[data-gs-brand-link]').forEach(a => { a.href = m.url || '/pages/find-my-bow'; });
        brandBox.querySelectorAll('[data-gs-brand-name]').forEach(n => { n.textContent = m.name; });
      }
      const g = isOurs ? ours() : null;
      numBox.hidden = !g;
      num.disabled = !g;
      numHint.textContent = !g ? '' : g.lookup
        ? 'Type it and we\'ll look up your bow\'s string length in our records. No number handy? Leave it blank and type your string length below.'
        : 'We\'re still adding ' + g.name + ' numbers to the site: type yours if you have it, and your string length below.';
      // waiting on the lookup: Add to cart waits too
      const key = recKey();
      num.setCustomValidity(key && key === looking ? 'Looking up your bow number, one moment.' : '');
      const s = v === 'oneida' ? null : pick();
      // a bow key with no string length on file (one of ours with none on its record, a brand model with no length):
      // what that bow's page sends, the customer's own length in the "not on file yet" box (Custom string length, _no_spec,
      // the can't-be-returned tick), and on our bows their tip to tip (Joey, Oct 18 2026)
      const nospec = !!(s && s.spec && !s.string);
      // the String Length box only steps aside for the "not on file yet" box when gstring.js could switch it on
      const quiet = nospec && !!(cbox && window.GSSetNospec);
      if (cbox && window.GSSetNospec) {
        window.GSSetNospec(cbox, nospec || cboxNospec);
        const tip = nospec && isOurs;
        tipParts.forEach(t => { t.hidden = !tip; });
        tipOff.forEach(t => { t.hidden = tip; });
        if (tipInput) { tipInput.disabled = !tip; tipInput.required = tip; }
        window.GSCustomCheck?.(cbox);
      }
      if (v === 'oneida') { answer.replaceChildren(); return; } // the Oneida box sends its own lines and says its own answer
      if (isString) setMaterial(s ? s.material : ''); // the Oneida box sets it for an Oneida
      fillTyped(s, v === 'other', quiet);
      send(sends.string, s && s.string ? s.string + '″' : '');
      send(sends.material, s ? s.material : '');
      pickSpec = s ? s.spec : '';
      setSpec();
      // said back (a status line, so screen readers hear it too). Never a cable length.
      const lines = [];
      if (s && s.pending) lines.push(para(['Looking up ' + s.bow + '…']));
      else if (s) {
        lines.push(para([bold(s.bow), s.string ? ': ' + s.string + '″ string' : '']));
        if (s.string) lines.push(para(['Built on ' + s.material + '.']));
        else if (isOurs && failed) lines.push(para(['We couldn\'t look up your number just now. Type your string length below, or try the number again.']));
        else if (isOurs && s.missing) lines.push(para(['We don\'t have ' + s.bow + ' online yet. Type your string length below: your number goes on the order so we can check it.']));
        // Joey, Oct 27 2026 (#220): "WE MAY HAVE THE SPEC BUT MAY HAVE TO LOOK BACK AT THE CUSTOMER OR MY PAPERS TO FIGURE IT OUT"
        else if (isOurs && s.found) lines.push(para(['We need to confirm this bow\'s string length. Enter your current string length and the bow\'s tip to tip below, and we\'ll check them against our records before we build. If they don\'t match what we have, we\'ll call or text you first.']));
        else if (isOurs && s.ours.lookup) lines.push(para(['Type your string length below, or your bow number above to look it up.']));
        else if (isOurs) lines.push(para(['Type your string length below.']));
        else lines.push(para(['We don\'t have this one\'s string length on file yet. Enter your current string length below.']));
      } else if (v === 'other') lines.push(para(['Type your bow and its string length below.']));
      else if (v === 'trad' && TRAD) {
        const a = document.createElement('a');
        a.href = TRAD;
        a.textContent = 'Flemish Twist String for Recurves and Longbows';
        lines.push(para(['Recurves and longbows have their own string: ', a, '.']));
      }
      answer.replaceChildren(...lines);
    };

    // a pick here is remembered for next time: an Oneida in its own box, a brand's model by its key, ours by its model
    // (and number, once it's found)
    const remember = () => {
      if (!mem) return;
      const g = ours();
      const m = theirs();
      if (g) mem.set(recKey() && looked[recKey()]?.found ? recKey() : g.key);
      else if (m) mem.set(m.h);
    };
    const forget = () => {
      if (remembered.hidden) return;
      remembered.hidden = true;
      brandSel.removeAttribute('aria-describedby');
    };
    onOneidaPick = forget;
    brandSel.addEventListener('change', () => { forget(); fillModels(); apply(); if (brandSel.value === 'oneida') oneida?.remember(); else remember(); lookup(); });
    modelSel.addEventListener('change', () => { forget(); fillSizes(); apply(); remember(); lookup(); });
    sizeSel.addEventListener('change', () => { forget(); apply(); });
    // typing a number: nothing is said until they stop or leave the box (a status line read out on every key is noise)
    let numTimer = 0;
    const numDone = () => { clearTimeout(numTimer); apply(); remember(); lookup(); };
    num.addEventListener('input', () => { forget(); clearTimeout(numTimer); numTimer = setTimeout(numDone, 700); });
    num.addEventListener('change', numDone);

    // start: a "what to order" link's bow, else the bow picked before
    const startOn = (v, model, n) => {
      brandSel.value = v;
      fillModels();
      if (model) { modelSel.value = model; fillSizes(); }
      if (n) num.value = n;
    };
    const fromKey = (key, n) => { // one of ours by its key ("g-rex", "g-rex-1285"), or a brand's model
      const g = OURS.find(x => key === x.key || key.startsWith(x.key + '-'));
      if (g) { startOn('gstring', g.key, n || (key.length > g.key.length ? numOf(key.slice(g.key.length + 1)) : '')); return true; }
      const m = allModels.find(x => x.h === key);
      if (m) { startOn('b:' + m.brand, m.h); return true; }
      return false;
    };
    let started = false;
    if (linkSpec && oneida && oneida.linkHit()) { startOn('oneida'); oneida.start('link'); started = true; }
    else if (linkSpec && fromKey(linkSpec, ctx.bow_number && numOf(ctx.bow_number))) started = true;
    else if (ctx.bow || ctx.string_length || ctx.bow_size) { if (typed.length) startOn('other'); started = true; } // any other bow from a link stays typed in
    if (!started) {
      const r = mem?.get();
      if (r && oneida && oneida.remembers()) { startOn('oneida'); oneida.start('mem'); started = true; }
      else if (r && fromKey(r.model)) started = true;
      if (started) { remembered.hidden = false; brandSel.setAttribute('aria-describedby', remembered.id); }
    }
    // a size from the link (a brand model page's size)
    if (started && ctx.bow_size && (theirs()?.sizes.length || 0) > 1) {
      const n = (theirs()?.sizes || []).findIndex(s => s.label === ctx.bow_size);
      if (n >= 0) sizeSel.value = String(n);
    }
    apply();
    lookup();
    brandBox.hidden = false;
  }

  /* ---------- Our own bows' cable listings: the number under the grip (Joey, Oct 27 2026, #177: "LIKE YOU RECOMMENDED").
     The number names the bow, so the order carries its key (g-rex-1285), the same one the bow's record page sends: the
     build sheet prints that bow's cable lengths, or flags the order for Joey (RECORD THE SPECS, the record-specs tag) when
     we have none on file. The key goes as soon as a number is typed, found online or not, so the shop's own records
     decide. The number is looked up like the bowstring's (/pages/bow/<key>?section_id=gs-bow-json) only to say back
     whether we have the bow online; the GSB's records aren't online yet, so its listings say we'll get it handled
     (#171, #242). It starts on a "what to order" link's number, else on the bow picked before (gs:bow "g-rex-1285").
     Never a length. ---------- */
  const numIn = root.querySelector('[data-gs-num-lookup]');
  if (numIn) {
    const model = numIn.dataset.gsNumLookup || '';
    const bowName = numIn.dataset.gsNumBow || '';
    const online = numIn.dataset.gsNumOnline === 'true';
    const out = root.querySelector('[data-gs-num-answer]');
    const looked = {};
    let looking = '';
    let failed = '';
    let fromMem = false;
    // just the number: "G-Rex #1285" or "grex 1285" is 1285. Our numbers are digits, or JC/W then digits (Gnats); the
    // GSB's aren't online, so letters may come first, but there's a digit. Anything else ("don't know", "none") sends no
    // key: the order is flagged as typed in, the same as before
    const numText = () => numOf(String(numIn.value || '').toUpperCase().replace(/^\s*(G-?\s?REX|PRO-?\s?2G|GNAT|GSB)/, '').replace(/^[\s#:-]+/, ''));
    const numOk = n => online ? /^(JC|W)?\d+$/.test(n) : /^(?=[A-Z0-9]{1,12}$)[A-Z]*\d/.test(n); // a GSB number has a digit too
    const keyOf = () => { const n = numText(); return model && n && numOk(n) ? model + '-' + slug(n) : ''; };
    const say = (...lines) => out?.replaceChildren(...lines.filter(Boolean).map(t => para([].concat(t))));
    const tell = () => {
      const before = fromMem ? 'Filled in with the bow you picked before. Change it if this is for another bow.' : '';
      if (!numIn.value.trim()) return say();
      const key = keyOf();
      if (!key) return say('Type just the number stamped under the grip' + (online ? ', like 1285.' : ', or leave the box blank.'));
      const bow = bowName + ' #' + numText();
      if (!online) return say(before, bow + ' goes on your order. We\'re still adding ' + bowName + ' specs and don\'t have yours on file yet, so we\'ll get it handled before we build.');
      const r = looked[key];
      if (r && r.found) {
        // the record is public, its cable lengths aren't: say we found the bow, never that we have its specs
        const a = document.createElement('a');
        a.className = 'gs-link';
        a.href = '/pages/bow/' + encodeURIComponent(key);
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = 'See its record';
        return say(before, 'We found ' + bow + ' in our records. Your number goes on the order, and we\'ll check its specs before we build.', [a, ' to make sure it\'s your bow.']);
      }
      if (r) return say(before, 'We don\'t have ' + bow + ' online yet. Your number goes on the order, and we\'ll look it up before we build.');
      if (failed === key) return say(before, 'We couldn\'t check ' + bow + ' just now. Your number still goes on the order, and we\'ll look it up before we build.');
      return say(before, 'Looking up ' + bow + '…');
    };
    const setKey = () => { pickSpec = keyOf(); setSpec(); };
    const lookup = async () => {
      const key = keyOf();
      if (!key || !online || key in looked || key === looking) { tell(); return; }
      looking = key;
      failed = '';
      tell(); // looking it up
      let r;
      try {
        const res = await fetch('/pages/bow/' + encodeURIComponent(key) + '?section_id=gs-bow-json', { signal: AbortSignal.timeout(8000) });
        const m = res.ok && (await res.text()).match(/<script type="application\/json" data-gs-bow-json>([\s\S]*?)<\/script>/);
        r = m ? JSON.parse(m[1]) : { found: false };
        if (!res.ok && res.status !== 404) r = { error: true }; // the store didn't answer: try again on the next change
      } catch (e) { r = { error: true }; }
      if (looking === key) looking = '';
      if (r.error) failed = key;
      else looked[key] = r;
      if (keyOf() !== key) { lookup(); return; } // another number was typed meanwhile: look that one up
      tell();
      if (r.found) mem?.set(key); // their bow, by its number, for next time
    };
    // the bow picked before, when it's one of this listing's bows with a number ("g-rex-1285"; "g-rex" alone has none)
    if (!numIn.value) {
      const k = String(mem?.get()?.model || '');
      const rest = k.startsWith(model + '-') ? k.slice(model.length + 1) : '';
      if (/^[a-z]{0,2}\d+$/.test(rest)) {
        numIn.value = numOf(rest);
        fromMem = true;
        if (numIn.required) needText(numIn);
      }
    }
    let timer = 0;
    numIn.addEventListener('input', () => { fromMem = false; setKey(); clearTimeout(timer); timer = setTimeout(lookup, 700); });
    numIn.addEventListener('change', () => { clearTimeout(timer); setKey(); lookup(); });
    window.addEventListener('pageshow', () => { setKey(); lookup(); }); // a number the browser put back fires no input
    setKey();
    lookup();
  }

  /* ---------- the sticky Add to cart bar clicks the real button; stop it first if something's missing ---------- */
  const formId = root.querySelector('[form]')?.getAttribute('form');
  window.addEventListener('click', e => {
    if (!e.target.closest('sticky-add-to-cart [ref="addToCartButton"], sticky-add-to-cart button')) return;
    const f = formId && document.getElementById(formId);
    if (!f || f.checkValidity()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const bad = [...root.querySelectorAll('select, input')].find(el => !el.checkValidity());
    if (bad) { bad.scrollIntoView({ behavior: 'smooth', block: 'center' }); bad.focus({ preventScroll: true }); bad.reportValidity(); }
  }, true);

  /* ---------- no finish on a pattern or on unpainted limbs (the shop's rule): the Finish choice turns off ---------- */
  const finBox = root.querySelector('[data-gs-finish]');
  if (finBox) {
    const nofin = new Set((finBox.dataset.gsNofin || '').split('|').filter(Boolean));
    const handle = v => String(v || '').toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const scope = root.closest('.shopify-section') || document;
    const sync = () => {
      const vals = [...scope.querySelectorAll('variant-picker input[type="radio"]:checked, variant-picker select')].map(i => handle(i.value));
      const none = vals.some(v => nofin.has(v));
      if ('gsAutoSolid' in finBox.dataset) { // follows the paint: Cerakote on a solid, film dip on a pattern, none on raw
        const raw = vals.some(v => v.includes('no-paint'));
        const v = none ? finBox.dataset.gsAutoPattern : finBox.dataset.gsAutoSolid;
        finBox.querySelectorAll('input').forEach(i => { i.value = v; i.disabled = raw; });
        finBox.querySelector('[data-gs-auto-text]').textContent = v;
        finBox.hidden = raw;
        return;
      }
      const ano = vals.some(v => /(^|-)anodize$/.test(v));
      finBox.hidden = none;
      finBox.querySelectorAll('select').forEach(s => { s.disabled = none; anodizeOnly(s, ano && !none); }); // a disabled choice isn't sent with the order
    };
    // a color that IS an anodize ("Clear anodize", "Yellow anodize": Joey, Oct 25 2026, "clear is anodize only") locks
    // the Finish choice to Anodize; any other color frees it again
    const anodizeOnly = (s, ano) => {
      const a = [...s.options].find(o => /^anodize/i.test(o.value));
      if (!a) return;
      [...s.options].forEach(o => { if (o.value) o.disabled = ano && o !== a; });
      if (ano) s.value = a.value;
    };
    // the picker redraws itself after a pick, so check again once it settles
    scope.addEventListener('change', e => { if (e.target.closest('variant-picker')) { sync(); setTimeout(sync, 400); setTimeout(sync, 1200); } });
    window.addEventListener('pageshow', sync);
    sync();
  }

  /* ---------- string picture ---------- */
  const art = root.querySelector('[data-gs-string-preview]');
  if (!art) return;
  // Close to the BCY 452X / rubber colors on screen. Mixes are drawn as both colors twisted: the store's old names
  // ("Red Black Mix", "Flo Yellow Black") and BCY 452X's own (Joey, Oct 25 2026, #146, from his BCY chart: "Red & Black",
  // "Fluo Orange-Black", "Fluor Green and Electric Blue"). "Flo", "Fluo", "Fluor" and "Fluorescent" are the same colors.
  // "Medium Brown / Tan" is one color, not a mix. Patriot, Autumn and the camos are drawn in their flecks' colors.
  const PALETTE = {
    gold: '#C9A43A', 'black cherry': '#5C1427', pink: '#F28DB2', flame: '#E8531F', 'light blue': '#8CC8EC', green: '#1F8A3B',
    'dark brown': '#4E3221', brown: '#6B4423', purple: '#5E2D91', black: '#17181A', gunmetal: '#50565C', red: '#C8102E',
    'royal blue': '#2341A8', blue: '#2E5CC7', 'sunset orange': '#F2712B', kiwi: '#8DBE3F', 'natural white': '#EFE8D8', white: '#F5F5F2',
    'od green': '#4B5320', 'olive drab': '#4B5320', 'electric blue': '#1E90FF', teal: '#128B8B', yellow: '#F2D21B', silver: '#B9BCC0',
    'flo yellow': '#E4FF2E', 'flo orange': '#FF6A1F', 'flo green': '#39E21C', 'flo purple': '#B026FF', 'flo pink': '#FF3FA4',
    buckskin: '#C9A27A', 'buck skin': '#C9A27A', tan: '#C8A97E', autumn: ['#B5651D', '#6B4423', '#C9A43A'], camo: ['#4B5320', '#6B4423', '#C8A97E', '#17181A'],
    // the rest of BCY 452X's chart (Joey's screenshot, Oct 25 2026), so a color added to the lists later draws too
    'mountain berry': '#8E1F33', 'root beer': '#7E4440', patriot: ['#B22234', '#F5F5F2', '#2341A8'], lavender: '#CFA2D6',
    'light pink': '#E8849C', 'electric red': '#E2231A', cedar: '#E8B25E', 'medium brown tan': '#A9784F', 'medium brown': '#A9784F',
    cocobolo: '#8A665A', 'metallic bronze': '#B36A48', seafoam: '#72CFE3', 'winter camo': ['#5B86A0', '#C9D3DA', '#3A4A55'],
    sand: '#B3ABA2', natural: '#D9CFB0',
  };
  const NAMES = Object.keys(PALETTE).sort((a, b) => b.length - a.length);
  const colorsOf = label => {
    let s = String(label || '').toLowerCase()
      .replace(/\b(fluorescent|fluor|fluo)\b/g, 'flo') // BCY's spellings of the store's "Flo" colors
      .replace(/\b(mix|and)\b/g, ' ').replace(/[&/,+\-–]+/g, ' ').replace(/\s+/g, ' ').trim(); // "&", "-", "and": a mix
    if (!s || s === 'none') return [];
    const out = [];
    while (s) {
      const hit = NAMES.find(n => s === n || s.startsWith(n + ' '));
      if (!hit) { s = s.replace(/^\S+\s*/, ''); continue; }
      out.push(...[].concat(PALETTE[hit]));
      s = s.slice(hit.length).trim();
    }
    return out;
  };
  const pick = re => {
    const sel = [...root.querySelectorAll('[data-gs-opt]')].find(s => re.test(s.dataset.gsOpt));
    return sel ? sel.value : '';
  };
  const NS = 'http://www.w3.org/2000/svg';
  const el = (name, attrs, parent) => {
    const n = document.createElementNS(NS, name);
    Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
    if (parent) parent.append(n);
    return n;
  };
  let uid = 0;
  function draw() {
    const strand = [...colorsOf(pick(/string colou?r one/i)), ...colorsOf(pick(/string colou?r two/i))];
    const strands = strand.length ? strand : ['#9AA0A6'];
    const whiskers = [...colorsOf(pick(/whisker colou?r one/i)), ...colorsOf(pick(/whisker colou?r two/i))];
    const slick = colorsOf(pick(/slick|finger saver/i));
    const id = 'gsStr' + (++uid);
    const svg = el('svg', { viewBox: '0 0 640 150', 'aria-hidden': 'true', focusable: 'false' });
    const defs = el('defs', {}, svg);
    // twisted strands: diagonal stripes cycling through the picked colors
    const w = 7 * Math.max(2, strands.length);
    const pat = el('pattern', { id, width: w, height: 40, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(58)' }, defs);
    const cycle = strands.length === 1 ? [strands[0], shade(strands[0], -0.18)] : strands;
    cycle.forEach((c, i) => el('rect', { x: (w / cycle.length) * i, y: 0, width: w / cycle.length + 0.5, height: 40, fill: c }, pat));
    const shine = el('linearGradient', { id: id + 'g', x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
    el('stop', { offset: 0, 'stop-color': '#fff', 'stop-opacity': '.35' }, shine);
    el('stop', { offset: '.5', 'stop-color': '#fff', 'stop-opacity': '0' }, shine);
    el('stop', { offset: 1, 'stop-color': '#000', 'stop-opacity': '.25' }, shine);
    const y = 75;
    // Flemish loops at both ends, then the twisted body
    [[58, 1], [582, -1]].forEach(([x, dir]) => {
      el('ellipse', { cx: x, cy: y, rx: 30, ry: 15, fill: 'none', stroke: `url(#${id})`, 'stroke-width': 6 }, svg);
      el('path', { d: `M${x + dir * 26} ${y} L${x + dir * 52} ${y}`, stroke: `url(#${id})`, 'stroke-width': 9, 'stroke-linecap': 'round' }, svg);
    });
    el('rect', { x: 104, y: y - 5, width: 432, height: 10, rx: 5, fill: `url(#${id})` }, svg);
    el('rect', { x: 104, y: y - 5, width: 432, height: 10, rx: 5, fill: `url(#${id}g)` }, svg);
    // whiskers (string silencers), one at each end; strands alternate the whisker colors
    if (whiskers.length) [176, 464].forEach(cx => {
      // a round rubber pom-pom, like the real ones
      for (let i = 0; i < 44; i++) {
        const a = (Math.PI * 2 * i) / 44 + (i % 2) * 0.05;
        const len = 30 + (i % 4) * 4;
        el('line', { x1: cx, y1: y, x2: cx + Math.cos(a) * len, y2: y + Math.sin(a) * len, stroke: whiskers[i % whiskers.length], 'stroke-width': 2.2, 'stroke-linecap': 'round' }, svg);
      }
      el('circle', { cx, cy: y, r: 7, fill: whiskers[0] }, svg);
    });
    // slick finger saver: a rubber block above and below the nocking point
    if (slick.length) [[296, 0], [336, 1]].forEach(([x]) => {
      el('rect', { x: x - 12, y: y - 9, width: 24, height: 18, rx: 6, fill: slick[0] }, svg);
      el('rect', { x: x - 12, y: y - 9, width: 24, height: 18, rx: 6, fill: `url(#${id}g)` }, svg);
    });
    if (slick.length) el('line', { x1: 316, y1: y - 13, x2: 316, y2: y + 13, stroke: '#14161A', 'stroke-opacity': '.35', 'stroke-width': 1.5, 'stroke-dasharray': '3 3' }, svg);
    art.replaceChildren(svg);
    const said = [pick(/string colou?r one/i), pick(/string colou?r two/i)].filter(Boolean).join(' and ') || 'no string color picked yet';
    const ws = [pick(/whisker colou?r one/i), pick(/whisker colou?r two/i)].filter(v => v && v !== 'None').join(' and ');
    const sl = pick(/slick|finger saver/i);
    art.setAttribute('aria-label', 'Picture of your string: ' + said + (ws ? ', whiskers ' + ws : '') + (sl && sl !== 'None' ? ', ' + sl + ' finger saver' : ''));
  }
  function shade(hex, amt) {
    const n = parseInt(hex.slice(1), 16);
    const f = c => Math.max(0, Math.min(255, Math.round(c + c * amt)));
    return '#' + [f(n >> 16), f((n >> 8) & 255), f(n & 255)].map(v => v.toString(16).padStart(2, '0')).join('');
  }
  root.addEventListener('change', e => { if (e.target.matches('[data-gs-opt]')) draw(); });
  draw();
})();
